'use client'

import { useSyncExternalStore } from 'react'
import { Moon, Sun } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { THEME_STORAGE_KEY } from '@/components/theme-script'

/**
 * The `<html>` class is the source of truth — ThemeScript set it before React ran — so the toggle
 * SUBSCRIBES to it rather than copying it into state. A MutationObserver is the subscription;
 * the server snapshot is light because the class cannot be known before the browser decides.
 */
function subscribe(onChange: () => void): () => void {
    const observer = new MutationObserver(onChange)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
}
const isDark = () => document.documentElement.classList.contains('dark')
const serverSnapshot = () => false

/** Light or dark, remembered per browser under the key ThemeScript reads, so a reload keeps it without a flash. */
export function ThemeToggle({ className }: { className?: string }) {
    const dark = useSyncExternalStore(subscribe, isDark, serverSnapshot)

    function toggle() {
        const next = !dark
        document.documentElement.classList.toggle('dark', next)
        try { localStorage.setItem(THEME_STORAGE_KEY, next ? 'dark' : 'light') } catch { /* storage blocked — the choice lasts the session */ }
    }

    return (
        <Button variant="ghost" size="icon" className={className} onClick={toggle} aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}>
            {dark ? <Sun /> : <Moon />}
        </Button>
    )
}
